import { useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Badge from "@/v2-backoffice-client/components/badge"
import Manage from "@/v2-backoffice-client/components/manage"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import LicenseStatusBadge from "@/v2-backoffice-client/license/LicenseStatusBadge"
import { useLicenseHistoryQuery } from "@/v2-backoffice-client/license/history/license.history.query"
import { getLicenseHistoryChanges } from "@/v2-backoffice-client/license/history/license.history.utils"
import { LICENSE_SOURCE_FENERUM } from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseHistoryMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  license: LicenseFragment
}

// Read-only audit trail: every temporal version of the license row, newest
// first, with what changed between versions (diffing lives in
// license.history.utils). Available on every license — including
// Fenerum-derived ones, where it answers "what did billing do and when".
function LicenseHistoryMenuItem(props: LicenseHistoryMenuItemProps) {
  const { license, children, ...menuItemProps } = props
  const [isOpen, setIsOpen] = useState(false)

  const { data, loading, error } = useLicenseHistoryQuery({
    variables: { id: license.Id },
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  })

  const entries = data?.getLicenseHistory ?? []
  const statusNames = new Map(
    (data?.getLicenseStatuses ?? []).map((status) => [
      status.Code,
      status.Name,
    ]),
  )

  return (
    <>
      <ActionMenu.Item
        iconSrc="clock"
        data-feature-code="UI_BackofficeLicense_Row_History"
        onSelect={(e) => {
          e.preventDefault()
          setIsOpen(true)
        }}
        {...menuItemProps}
      >
        {children ?? "View History"}
      </ActionMenu.Item>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Content align="top" className="max-w-2xl">
          <Dialog.Header>
            <Dialog.Title>License History</Dialog.Title>
            <Dialog.Description>
              {license.CustomerName ?? "This customer"} · {license.ProductName}
              {" — every version of the license, newest first."}
            </Dialog.Description>
          </Dialog.Header>

          <ErrorMessage className="my-4 text-center" error={error} />

          {loading && entries.length === 0 && (
            <Manage.Skeleton count={3} aria-busy="true" />
          )}

          <ol className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
            {entries.map((entry, index) => {
              // Entries are newest-first; the previous version is the next
              // item. The oldest entry has no predecessor: it is the grant.
              const previous = entries[index + 1]
              const changes = getLicenseHistoryChanges(entry, previous)
              const isStatusChange =
                previous !== undefined &&
                previous.LicenseStatusCode !== entry.LicenseStatusCode
              const isSyncWrite =
                previous !== undefined &&
                (entry.SyncedAt ?? null) !== (previous.SyncedAt ?? null)

              return (
                <li
                  key={entry.VersionStart}
                  className="rounded-md border border-muted p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">
                      {formatDateString(entry.VersionStart, "dd.MM.yyyy HH:mm")}
                    </span>

                    <LicenseStatusBadge
                      license={{
                        LicenseStatusCode: entry.LicenseStatusCode,
                        // Historical versions must not render a live
                        // countdown — the clock only means something on the
                        // current row.
                        TrialEndsAt: null,
                      }}
                      statusName={statusNames.get(entry.LicenseStatusCode)}
                    />

                    {entry.VersionEnd === null && (
                      <Badge variant="outline">Current</Badge>
                    )}

                    <span className="ml-auto text-xs text-muted-foreground">
                      {entry.UpdatedByUserName ??
                        (entry.LicenseSourceCode === LICENSE_SOURCE_FENERUM ||
                        isSyncWrite
                          ? "Billing sync"
                          : "—")}
                    </span>
                  </div>

                  {previous === undefined ? (
                    <p className="mt-2 text-sm text-muted-foreground">
                      License created
                      {entry.PlanName ? ` on ${entry.PlanName}` : ""}
                      {entry.Seats !== null && entry.Seats !== undefined
                        ? ` with ${entry.Seats} seats`
                        : ""}
                      .
                    </p>
                  ) : (
                    <ul className="mt-2 space-y-1 text-sm">
                      {isStatusChange && (
                        <li className="flex items-center gap-2">
                          <span className="w-24 shrink-0 text-muted-foreground">
                            Status
                          </span>
                          <span>
                            {statusNames.get(previous.LicenseStatusCode) ??
                              previous.LicenseStatusCode}
                            {" → "}
                            {statusNames.get(entry.LicenseStatusCode) ??
                              entry.LicenseStatusCode}
                          </span>
                        </li>
                      )}
                      {changes.map((change) => (
                        <li
                          key={change.label}
                          className="flex items-center gap-2"
                        >
                          <span className="w-24 shrink-0 text-muted-foreground">
                            {change.label}
                          </span>
                          <span>
                            {change.from} → {change.to}
                          </span>
                        </li>
                      ))}
                      {!isStatusChange && changes.length === 0 && (
                        <li className="text-muted-foreground">
                          Synced — no visible field changed.
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              )
            })}
          </ol>

          {!loading && entries.length === 0 && !error && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No history recorded for this license.
            </p>
          )}
        </Dialog.Content>
      </Dialog>
    </>
  )
}

export default LicenseHistoryMenuItem
